import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { FSAccountOption, MEOption } from '../cbc-reconciliation/cbc-reconciliation.service';
import { CbcReconciliationService } from '../cbc-reconciliation/cbc-reconciliation.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-cbc-create-fs-elim-data',
    templateUrl: './cbc-create-fs-elim-data.component.html',
    styleUrls: ['./cbc-create-fs-elim-data.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CbcCreateFsElimDataComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  modalTitle = 'Financial Statements - Add/Edit Entry';
  minimise = false;
  saveLoading = false;

  ME: MEOption[] = [];
  selectedME: string | null = null;
  fsAccounts: FSAccountOption[] = [];
  selectedFSAcc: string | null = null;
  meSelected = false;
  fsAcc = false;

  amountTypeOptions = [
    { value: 0, name: '' },
    { value: 100, name: 'Hundreds' },
    { value: 1000, name: 'Thousands' },
    { value: 1000000, name: 'Millions' },
  ];
  amountTypeSelected = false;
  selectedAmountType = 0;
  amount1: number | null = 0;
  amount2: number | null = 0;
  amount3: number | null = 0;

  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  private isInitialized = false;
  isLoading = false;

  constructor(
    private cbcService: CbcReconciliationService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.markForCheck();
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.GENERAL_CONFIG?.base_url || !this.filterParams || Object.keys(this.filterParams).length === 0
    || !this.globalService || Object.keys(this.globalService).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    this.isLoading = true;
    this.cdr.detectChanges();
    this.cbcService.getMEData(this.baseUrl, this.filterParams, this.ssoId, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        if (response && response.length > 0) {
          this.ME = response;
          const parentMe = this.filterParams?.parent_me_string;
          const meKeyArray = parentMe ? String(parentMe).split(',') : [];
          if (meKeyArray.length > 0 && meKeyArray.length !== this.ME.length) {
            for (const element of this.ME) {
              for (let i = 0; i < meKeyArray.length; i++) {
                if (String(element.ME_KEY) === meKeyArray[i]) {
                  this.selectedME = element.ME_CODE;
                  break;
                }
              }
              if (this.selectedME) break;
            }
          } else if (meKeyArray.length > 0) {
            const meKey = meKeyArray.length > 1 ? meKeyArray[1] : meKeyArray[0];
            const findME = this.ME.find((elem) => String(elem.ME_KEY) === meKey);
            if (findME) this.selectedME = findME.ME_CODE;
          }
          if (!this.selectedME && this.ME.length) {
            this.selectedME = this.ME[0].ME_CODE;
          }
          this.onMESelect();
          this.cbcService.getFSAccounts(this.baseUrl, this.filterParams, this.ssoId, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (accResp) => {
              this.fsAccounts = accResp;
              this.selectedFSAcc = accResp.length > 0 ? accResp[0].ACCT : null;
              this.onFSAccountSelect();
              this.showForm();
            },
            error: (err) => {
              this.gtwSnackbarService.openSnackBar(err?.message || 'Failed to load FS accounts', 'gtw-snackbar--red');
              this.showForm();
            },
          });
        } else {
          this.showForm();
        }
      },
      error: (err) => {
        this.gtwSnackbarService.openSnackBar(err?.message || 'Failed to load ME data', 'gtw-snackbar--red');
        this.showForm();
      },
    });
  }

  private showForm(): void {
    setTimeout(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    });
  }

  onMEChange(value: string | null): void {
    this.selectedME = value;
    this.onMESelect();
  }

  onFSAccChange(value: string | null): void {
    this.selectedFSAcc = value;
    this.onFSAccountSelect();
  }

  onAmountTypeChange(value: number): void {
    this.selectedAmountType = value;
    this.amountTypeChange();
  }

  onMESelect(): void {
    this.meSelected = true;
    if (this.fsAcc && this.selectedME && this.selectedFSAcc) {
      this.cbcService
        .getEntryData(this.baseUrl, this.filterParams, this.ssoId, this.selectedME, this.selectedFSAcc, null, this.globalService).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            if (response && response.length > 0) {
              this.amount1 = response[0].AMOUNT_1204 ?? 0;
              this.amount2 = response[0].AMOUNT_1205 ?? 0;
              this.amount3 = response[0].AMOUNT_1206 ?? 0;
            } else {
              this.amount1 = this.amount2 = this.amount3 = 0;
            }
            this.cdr.detectChanges();
          },
          error: () => {
            this.amount1 = this.amount2 = this.amount3 = 0;
            this.cdr.detectChanges();
          },
        });
    }
    this.cdr.detectChanges();
  }

  onFSAccountSelect(): void {
    this.fsAcc = true;
    if (this.selectedME && this.selectedFSAcc) {
      this.cbcService
        .getEntryData(this.baseUrl, this.filterParams, this.ssoId, this.selectedME, this.selectedFSAcc, null, this.globalService).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            if (response && response.length > 0) {
              this.amount1 = response[0].AMOUNT_1204 ?? 0;
              this.amount2 = response[0].AMOUNT_1205 ?? 0;
              this.amount3 = response[0].AMOUNT_1206 ?? 0;
            } else {
              this.amount1 = this.amount2 = this.amount3 = 0;
            }
            this.cdr.detectChanges();
          },
          error: () => {
            this.amount1 = this.amount2 = this.amount3 = 0;
            this.cdr.detectChanges();
          },
        });
    }
    this.cdr.detectChanges();
  }

  amountTypeChange(): void {
    this.amountTypeSelected = true;
    this.cdr.detectChanges();
  }

  parseAmount(v: number | string | null): number {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    const s = String(v).replace(/[^\d.\-]/g, '');
    const n = parseFloat(s);
    return Number.isNaN(n) ? 0 : n;
  }

  save(): void {
    if (this.saveLoading || !this.selectedME || !this.selectedFSAcc) return;
    const amt1 = this.parseAmount(this.amount1);
    const amt2 = this.parseAmount(this.amount2);
    const amt3 = this.parseAmount(this.amount3);
    const finalAmount1 = this.amountTypeSelected ? amt1 * this.selectedAmountType : amt1;
    const clobData = [
      {
        ME_CODE: this.selectedME,
        ACCT: this.selectedFSAcc,
        AMOUNT_1204: finalAmount1,
        AMOUNT_1205: amt2,
        AMOUNT_1206: amt3,
        AMOUNT_1207: null,
        COMMENTS: null,
        CATEGORY: null,
        VARIANCE_TYPE: null,
        POPUP_KEY: 1,
      },
    ];
    this.saveLoading = true;
    this.cdr.detectChanges();
    this.cbcService.saveData(this.baseUrl, clobData, this.filterParams, this.ssoId, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.saveLoading = false;
        if (response?.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Data is successfully updated.', 'gtw-snackbar--green');
          this.cancel();
          setTimeout(() => {
            const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
            if (activeScreen?.screen_key) {
              this.gtwWorkspace.refreshGridData.next(activeScreen);
            }
          }, 500);
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.saveLoading = false;
        this.gtwSnackbarService.openSnackBar(err?.message || 'Error saving.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true,
    });
    document
      .querySelector('gtw-cbc-create-fs-elim-data-ce')
      ?.dispatchEvent(updateEvent);
  }
}
